import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import SimOpsAPI, { getSimOpsErrorMessage } from 'api/ops/v2/SimOpsAPI'
import { RoundedButton } from 'common/Button/Button'
import NormalText from 'common/Typography/NormalText'

import { refetchUserEsimQueries } from '../simOpsQueries'

function AllocateEsimDialog({
  isDialogOpen,
  handleClose,
  userId
}: {
  isDialogOpen: boolean
  handleClose: () => void
  userId: string
}) {
  const queryClient = useQueryClient()
  const [esimConfigId, setEsimConfigId] = useState('0')
  const [terminateOld, setTerminateOld] = useState(false)

  useEffect(() => {
    setEsimConfigId('0')
    setTerminateOld(false)
  }, [isDialogOpen])

  const { mutate: allocateEsim, isLoading } = useMutation(
    SimOpsAPI.allocateEsim,
    {
      onSuccess: (res) => {
        toast.success(
          `eSIM ${res.data?.data?.iccid ?? ''} allocated successfully`
        )
        refetchUserEsimQueries(queryClient, userId)
        handleClose()
      },
      onError: (error) => {
        toast.error(getSimOpsErrorMessage(error, 'Failed to allocate eSIM'))
      }
    }
  )

  const handleAllocateEsim = () => {
    allocateEsim({
      userId,
      esimConfigId: Number(esimConfigId) || 0,
      terminateOld
    })
  }

  return (
    <Dialog
      onClose={handleClose}
      open={isDialogOpen}
      aria-labelledby='allocate-esim-dialog'
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>Allocate eSIM</DialogTitle>
      <DialogContent>
        <NormalText>
          Allocates a new eSIM from inventory to this customer. Use this for
          customers with a missing eSIM QR code (i.e. if the eSIM status is
          N/A).
        </NormalText>
        <NormalText>
          After allocating the eSIM, please use the resend esim email function
          to resend the esim qr code to the customer.
        </NormalText>
        <TextField
          label='eSIM Config ID'
          type='number'
          fullWidth
          margin='normal'
          value={esimConfigId}
          onChange={(e) => setEsimConfigId(e.target.value)}
          helperText='Leave as 0 to use the default Telna config (whitelist 11906)'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={terminateOld}
              onChange={(e) => setTerminateOld(e.target.checked)}
            />
          }
          label="Terminate the customer's current eSIM"
        />
      </DialogContent>
      <DialogActions>
        <RoundedButton variant='outlined' onClick={handleClose}>
          Cancel
        </RoundedButton>
        <LoadingButton
          variant='contained'
          onClick={handleAllocateEsim}
          loading={isLoading}
        >
          Allocate
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default AllocateEsimDialog
